import type { List, ListEntry, Patient, Reference } from '@medplum/fhirtypes'
import { invariant } from '#src/invariant.ts'
import { ExtendedList } from '#src/models/list.ts'

export abstract class BaseMealQueue extends ExtendedList {
  constructor(
    data?: Omit<List, 'resourceType' | 'mode' | 'status'> &
      Partial<Pick<List, 'status'>>
  ) {
    super({
      title: data?.title ?? 'Meal Queue',
      ...data,
    })
  }

  get entries(): ListEntry[] {
    return this.resource.entry ?? []
  }

  get participants(): Reference<Patient>[] {
    return this.entries.map((entry) => entry.item as Reference<Patient>)
  }

  get length(): number {
    return this.entries.length
  }

  get isEmpty(): boolean {
    return this.length === 0
  }

  get firstParticipant(): Reference<Patient> | undefined {
    return this.entries[0]?.item as Reference<Patient> | undefined
  }

  addParticipant(participant: Reference<Patient>): void {
    invariant(participant.reference, 'Participant must have a valid reference')

    // Check if participant is already in queue
    const existingIndex = this.entries.findIndex(
      (entry) => entry.item.reference === participant.reference
    )
    if (existingIndex !== -1) {
      return
    }

    this.resource.entry ??= []
    this.resource.entry.push({
      item: participant,
      date: new Date().toISOString(),
    })
  }

  removeParticipant(participant: Reference<Patient>): void {
    invariant(participant.reference, 'Participant must have a valid reference')

    if (!this.resource.entry) {
      return
    }

    this.resource.entry = this.resource.entry.filter(
      (entry) => entry.item.reference !== participant.reference
    )
  }

  removeFirstParticipant(): Reference<Patient> | undefined {
    if (!this.resource.entry || this.resource.entry.length === 0) {
      return undefined
    }

    const firstEntry = this.resource.entry.shift()
    return firstEntry?.item as Reference<Patient> | undefined
  }

  moveParticipantToPosition(
    participant: Reference<Patient>,
    newPosition: number
  ): void {
    invariant(participant.reference, 'Participant must have a valid reference')
    invariant(newPosition >= 0, 'Position must be non-negative')

    if (!this.resource.entry) {
      return
    }

    const currentIndex = this.resource.entry.findIndex(
      (entry) => entry.item.reference === participant.reference
    )
    if (currentIndex === -1) {
      return
    }

    const entry = this.resource.entry.splice(currentIndex, 1)[0]
    const insertIndex = Math.min(newPosition, this.resource.entry.length)
    this.resource.entry.splice(insertIndex, 0, entry)
  }

  getParticipantPosition(participant: Reference<Patient>): number {
    invariant(participant.reference, 'Participant must have a valid reference')

    return this.entries.findIndex(
      (entry) => entry.item.reference === participant.reference
    )
  }

  getParticipantQueueTime(participant: Reference<Patient>): Date | undefined {
    invariant(participant.reference, 'Participant must have a valid reference')

    const entry = this.entries.find(
      (entry) => entry.item.reference === participant.reference
    )
    return entry?.date ? new Date(entry.date) : undefined
  }

  clear(): void {
    this.resource.entry = []
  }
}
